import React from "react";
import { Navigate } from "react-router-dom";
import { useAppStore } from "../../stores/useAppStore";
import PageLoader from "../ui/pageLoader";

export default function ProtectedRoute({ children }) {
  const authToken = useAppStore(state => state.authToken);
  const authHydrated = useAppStore(state => state.authHydrated);

  // Prevent redirect until hydration completes. Show a spinner instead of
  // an empty div so the user sees something during the brief auth handoff.
  if (!authHydrated) {
    return <PageLoader text="Signing you in…" />;
  }

  if (!authToken) {
    return <Navigate to="/" replace />;
  }

  return children;
}

/**
 * RoleRoute — enforces role-based access on top of auth.
 *
 * roles: array of allowed roles, e.g. ['manager'] or ['admin']
 *
 * Roles are additive: a user who is both admin AND manager passes either
 * gate. A manager-without-admin can't reach admin pages; an admin-without-
 * manager can't reach manager pages. Off-scope users are redirected to
 * their home page (admin → /app/admin, manager → /app/dashboards) rather
 * than seeing a blank screen.
 */
export function RoleRoute({ children, roles = [] }) {
  const currentUser = useAppStore(state => state.currentUser);
  const authHydrated = useAppStore(state => state.authHydrated);

  if (!authHydrated) return <PageLoader text="Signing you in…" />;

  if (!currentUser) return <Navigate to="/" replace />;

  const allowed =
    (roles.includes('manager') && currentUser.isManager) ||
    (roles.includes('admin') && currentUser.isAdmin);

  if (!allowed) {
    // Prefer the manager home when the user has the manager role (or is
    // admin+manager) since they're more likely to be doing day-to-day
    // work there; pure-admins land on /app/admin.
    if (currentUser.isManager) return <Navigate to="/app/dashboards" replace />;
    if (currentUser.isAdmin) return <Navigate to="/app/admin" replace />;
    return <Navigate to="/" replace />;
  }

  return children;
}
